// UPDATED studentsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const initialState = {
  students: null,
  filteredStudents: null,
  filterValues: {
    id: '',
    first_name: '',
    second_name: '',
    school_class: '',
    onlyUnpaid: false
  },
  selectedStudent: null,
  unpaidStudents: [],
  unpaidStudentsStatus: 'idle',
  unpaidStudentsError: null, 
  selectedStudentStatus: 'idle',
  selectedStudentError: null,
  deleteStatus: 'idle',
  deleteError: null,
  status: 'idle',
  error: null,
  archiveStatus: 'idle',
  archiveError: null,
  validationStatus: 'idle',
  validationError: null,
  promotionPreview: null,
};

// Fetch students (simulate API)
export const fetchStudents = createAsyncThunk('students/fetchStudents', async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/?${params}`);
  return response.data.map(student => ({
    ...student,
    isArchived: student.is_archived,
  }));
});

export const fetchStudentsPageData = createAsyncThunk(
  'students/fetchStudentsPageData', 
  async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/page-data/?${params}`);
    return response.data;
  }
);

export const fetchUnpaidStudents = createAsyncThunk(
  'students/fetchUnpaidStudents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/unpaid/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في جلب الطلاب الغير مسددين');
    }
  }
);

export const deleteStudent = createAsyncThunk(
  'students/deleteStudent',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/${studentId}/delete/`
      );
      return { studentId, message: response.data.message };
    } catch (err) {
      // Handle different error scenarios
      const errorData = err.response?.data || {};
      
      if (errorData.suggestion === 'archive') {
        // Backend suggests archiving instead of deleting
        return rejectWithValue({
          type: 'SUGGEST_ARCHIVE',
          message: errorData.error,
          hasPaymentHistory: errorData.has_payment_history,
          hasActivePayments: errorData.has_active_payments
        });
      }
      
      const errorMessage = errorData.error || 
                          errorData.detail || 
                          errorData.message ||
                          'خطأ في حذف الطالب';
      return rejectWithValue({
        type: 'DELETE_ERROR',
        message: errorMessage
      });
    }
  }
);

export const archiveStudent = createAsyncThunk(
  'students/archiveStudent',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/${studentId}/`,
        { is_archived: true }
      );
      return response.data;
    } catch (err) {
      // Handle validation errors from backend
      const errorMessage = err.response?.data?.is_archived?.[0] || 
                          err.response?.data?.detail || 
                          err.response?.data?.message ||
                          'خطأ في الأرشفة';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createStudent = createAsyncThunk(
  'students/createStudent',
  async (studentData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/students/`, studentData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const validateStudentId = createAsyncThunk(
  'students/validateStudentId',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/`, {
        params: { is_archived: false }
      });

      const duplicateStudent = response.data.find(
        std => std.student_id === studentId
      );

      if (duplicateStudent) {
        return {
          isDuplicate: true,
          student: duplicateStudent
        };
      } else {
        return {
          isDuplicate: false,
          student: null
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'فشل في التحقق من رقم الهوية');
    }
  }
);

export const fetchStudentById = createAsyncThunk(
  'students/fetchById',
  async (studentId) => {
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/${studentId}/`);
    return response.data;
  }
);

export const closeStudentAccount = createAsyncThunk(
  'students/closeStudentAccount',
  async ({ studentId, year }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`/api/students/close-account/${studentId}/`, { year });
      
      await dispatch(fetchStudentById(studentId));
      
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في تسكير الحساب');
    }
  }
);

export const updateStudent = createAsyncThunk(
  'students/updateStudent',
  async ({ id, updatedData }) => {
    const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/students/${id}/`, updatedData);
    return response.data;
  }
);

export const bulkTransferStudents = createAsyncThunk(
  'students/bulkTransferStudents',
  async ({ studentIds, targetClassId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/bulk-transfer/`,
        {
          student_ids: studentIds,
          target_class_id: targetClassId
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في نقل الطلاب');
    }
  }
);

export const getPromotionPreview = createAsyncThunk(
  'students/getPromotionPreview',
  async (excludeIds = [], { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      excludeIds.forEach(id => params.append('exclude_ids', id));
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/promotion/preview/?${params.toString()}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في جلب معاينة الترقية');
    }
  }
);

export const executePromotion = createAsyncThunk(
  'students/executePromotion',
  async (promotions, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/promotion/execute/`,
        { promotions }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في تنفيذ الترقية');
    }
  }
);

export const unarchiveStudent = createAsyncThunk(
  'students/unarchiveStudent',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/${studentId}/`,
        { is_archived: false }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'فشل في الاسترجاع من الأرشيف');
    }
  }
);

const studentsSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    setFilterValues: (state, action) => {
      state.filterValues = action.payload;

      if (!state.students) return;

      state.filteredStudents = state.students.filter((student) => {
        // ID filter
        const matchesId = !state.filterValues.id || 
          String(student.student_id).includes(state.filterValues.id);

        // First name filter
        const matchesFirstName = !state.filterValues.first_name || 
          student.first_name?.toLowerCase().includes(state.filterValues.first_name.toLowerCase());

        // Second name filter  
        const matchesSecondName = !state.filterValues.second_name || 
          student.second_name?.toLowerCase().includes(state.filterValues.second_name.toLowerCase());

        // Class filter
        const matchesClass = !state.filterValues.school_class || 
          student.school_class?.toString().includes(state.filterValues.school_class);

           const matchesAddress = !state.filterValues.address || 
          (student.address || 'فلسطين').toLowerCase().includes(state.filterValues.address.toLowerCase());

        // Unpaid filter - FIXED: Handle payment_summary as object
        const matchesUnpaid = !state.filterValues.onlyUnpaid || (() => {
          // Handle different payment summary formats
          if (student.payment_summary && typeof student.payment_summary === 'object') {
            const totalPaid = student.payment_summary.total_paid || 0;
            const totalFee = student.payment_summary.total_fee || 0;
            return totalPaid < totalFee;
          }
          
          // Fallback to old format if still using strings
          if (typeof student.payment_summary === 'string' && student.payment_summary.includes('/')) {
            const [paid, total] = student.payment_summary.split('/').map(Number);
            return paid < total;
          }
          
          // Fallback to individual fields
          const totalPaid = student.paid_sum || 0;
          const totalFee = student.total_fees || 0;
          return totalPaid < totalFee;
        })();

        return matchesId && matchesFirstName && matchesSecondName && matchesClass && matchesUnpaid && matchesAddress;
      });
    },
    clearArchiveError: (state) => {
      state.archiveError = null;
      state.archiveStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.students = action.payload;
        state.filteredStudents = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchStudentsPageData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStudentsPageData.fulfilled, (state, action) => {
        state.students = action.payload.results;
        state.filteredStudents = action.payload.results;
        state.status = 'succeeded';
      })
      .addCase(fetchStudentsPageData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        if (state.students) {
          state.students.push(action.payload);
          state.filteredStudents = [...state.students];
        }
      })
      // Archive student cases
      .addCase(archiveStudent.pending, (state) => {
        state.archiveStatus = 'loading';
        state.archiveError = null;
      })
      .addCase(archiveStudent.fulfilled, (state, action) => {
        state.archiveStatus = 'succeeded';
        state.archiveError = null;
        
        const updated = action.payload;
        if (state.students) {
          const index = state.students.findIndex(s => s.id === updated.id);
          if (index !== -1) {
            state.students[index] = updated;
          }
          
          // Filter out archived students for non-archived view
          state.students = state.students.filter(student => !student.is_archived);
          state.filteredStudents = state.students.filter((student) => {
            const matchesId = !state.filterValues.id || 
              String(student.student_id).includes(state.filterValues.id);
            const matchesFirstName = !state.filterValues.first_name || 
              student.first_name?.toLowerCase().includes(state.filterValues.first_name.toLowerCase());
            const matchesSecondName = !state.filterValues.second_name || 
              student.second_name?.toLowerCase().includes(state.filterValues.second_name.toLowerCase());
            const matchesClass = !state.filterValues.school_class || 
              student.school_class?.toString().includes(state.filterValues.school_class);
            
            return matchesId && matchesFirstName && matchesSecondName && matchesClass;
          });
        }
      })
      .addCase(archiveStudent.rejected, (state, action) => {
        state.archiveStatus = 'failed';
        state.archiveError = action.payload;
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        if (state.students) {
          const index = state.students.findIndex(s => s.id === action.payload.id);
          if (index !== -1) {
            state.students[index] = action.payload;
          }
          state.filteredStudents = [...state.students];
        }
      })
      .addCase(unarchiveStudent.fulfilled, (state, action) => {
        const updated = action.payload;
        if (state.students) {
          const index = state.students.findIndex(s => s.id === updated.id);
          if (index !== -1) {
            state.students[index] = updated;
          }

          state.filteredStudents = state.students.filter((student) => {
            const matchesId = !state.filterValues.id || 
              String(student.student_id).includes(state.filterValues.id);
            const matchesFirstName = !state.filterValues.first_name || 
              student.first_name?.toLowerCase().includes(state.filterValues.first_name.toLowerCase());
            const matchesSecondName = !state.filterValues.second_name || 
              student.second_name?.toLowerCase().includes(state.filterValues.second_name.toLowerCase());
            const matchesClass = !state.filterValues.school_class || 
              student.school_class?.toString().includes(state.filterValues.school_class);
            
            return matchesId && matchesFirstName && matchesSecondName && matchesClass;
          });
        }
      })
      .addCase(fetchStudentById.pending, (state) => {
        state.selectedStudentStatus = 'loading';
        state.selectedStudentError = null;
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.selectedStudentStatus = 'succeeded';
        state.selectedStudent = action.payload;
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.selectedStudentStatus = 'failed';
        state.selectedStudentError = action.error.message;
      })
      .addCase(fetchUnpaidStudents.pending, (state) => {
        state.unpaidStudentsStatus = 'loading';
        state.unpaidStudentsError = null;
      })
      .addCase(validateStudentId.pending, (state) => {
        state.validationStatus = 'validating';
        state.validationError = null;
      })
      .addCase(validateStudentId.fulfilled, (state) => {
        state.validationStatus = 'succeeded';
      })
      .addCase(validateStudentId.rejected, (state, action) => {
        state.validationStatus = 'failed';
        state.validationError = action.payload;
      })
      .addCase(deleteStudent.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.deleteError = null;
        
        const { studentId } = action.payload;
        if (state.students) {
          // Remove the deleted student from the list
          state.students = state.students.filter(student => student.id !== studentId);
          state.filteredStudents = state.students.filter((student) => {
            const matchesId = !state.filterValues.id || 
              String(student.student_id).includes(state.filterValues.id);
            const matchesFirstName = !state.filterValues.first_name || 
              student.first_name?.toLowerCase().includes(state.filterValues.first_name.toLowerCase());
            const matchesSecondName = !state.filterValues.second_name || 
              student.second_name?.toLowerCase().includes(state.filterValues.second_name.toLowerCase());
            const matchesClass = !state.filterValues.school_class || 
              student.school_class?.toString().includes(state.filterValues.school_class);
            
            return matchesId && matchesFirstName && matchesSecondName && matchesClass;
          });
        }
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      })
      .addCase(fetchUnpaidStudents.fulfilled, (state, action) => {
        state.unpaidStudentsStatus = 'succeeded';
        state.unpaidStudents = action.payload;
      })
      .addCase(fetchUnpaidStudents.rejected, (state, action) => {
        state.unpaidStudentsStatus = 'failed';
        state.unpaidStudentsError = action.payload;
      })
      .addCase(closeStudentAccount.pending, (state) => {
        state.status = 'closing';
      })
      .addCase(closeStudentAccount.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(closeStudentAccount.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(bulkTransferStudents.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bulkTransferStudents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Refresh students list after transfer
        if (state.students) {
          // Update transferred students' class in local state
          action.payload.transferred?.forEach(transferred => {
            const student = state.students.find(s => s.id === transferred.id);
            if (student) {
              student.school_class = action.payload.target_class_id;
            }
          });
        }
      })
      .addCase(bulkTransferStudents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getPromotionPreview.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPromotionPreview.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.promotionPreview = action.payload;
      })
      .addCase(getPromotionPreview.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(executePromotion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(executePromotion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Refresh students list after promotion
        if (state.students) {
          action.payload.promoted?.forEach(promoted => {
            const student = state.students.find(s => s.id === promoted.id);
            if (student) {
              student.school_class = action.payload.new_class_id;
            }
          });
        }
      })
      .addCase(executePromotion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { setFilterValues, clearArchiveError } = studentsSlice.actions;
export default studentsSlice.reducer;